import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router-dom';
import type { BugReviewItem } from '@sr-test-wizard/shared';
import { JiraKeyLink } from '../../components/JiraKeyLink';
import { useBugReviewStore } from '../../state/bugReviewStore';
import { BugActionsBar } from './BugActionsBar';
import { BugCard } from './BugCard';
import { BugChat } from './BugChat';
import { CommentsThread } from './CommentsThread';

function applyFilter(
  items: BugReviewItem[],
  filter: 'all' | 'open' | 'done',
  query: string,
): BugReviewItem[] {
  const q = query.trim().toLowerCase();
  return items.filter((item) => {
    if (filter === 'open' && item.isDone) return false;
    if (filter === 'done' && !item.isDone) return false;
    if (!q) return true;
    const haystack = [
      item.key,
      item.summary,
      item.status,
      item.priority,
      item.assignee,
      item.linkedTestKey,
      item.labels.join(' '),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  });
}

/**
 * Bug review pane (rail "Extras" section). Two-column layout: left rail =
 * filtered list of bugs linked to the TEXEC (or raised through the app),
 * right pane = detail + comments + per-bug chat + action bar (re-pull
 * source, upload image, post comment, transition status).
 *
 * The list is loaded lazily on mount; selecting a bug fetches detail + chat
 * in parallel. Live updates from the bug-update WS event keep status /
 * comment counts in sync without re-reading the bundle.
 *
 * NOTE: this panel still lives under `features/phase5/` for git-history
 * reasons; it is no longer numbered as a workflow phase and has its own
 * `/runs/:id/bugs` route. See `App.tsx` for the URL <-> folder mapping.
 */
export function BugReviewPanel() {
  const { id } = useParams<{ id: string }>();
  const runId = id!;
  const bundle = useBugReviewStore((s) => s.bundle);
  const loading = useBugReviewStore((s) => s.loading);
  const error = useBugReviewStore((s) => s.error);
  const load = useBugReviewStore((s) => s.load);
  const select = useBugReviewStore((s) => s.select);
  const selectedKey = useBugReviewStore((s) => s.selectedKey);
  const detailByKey = useBugReviewStore((s) => s.detailByKey);
  const detailLoading = useBugReviewStore((s) => s.detailLoading);
  const detailError = useBugReviewStore((s) => s.detailError);
  const chatByKey = useBugReviewStore((s) => s.chatByKey);
  const chatBusy = useBugReviewStore((s) => s.chatBusy);
  const sendChat = useBugReviewStore((s) => s.sendChat);
  const filter = useBugReviewStore((s) => s.filter);
  const setFilter = useBugReviewStore((s) => s.setFilter);

  const [search, setSearch] = useState('');

  useEffect(() => {
    if (runId) void load(runId);
  }, [runId, load]);

  const refresh = () => load(runId, { force: true });

  const filteredItems = useMemo(() => {
    if (!bundle) return [];
    return applyFilter(bundle.items, filter, search);
  }, [bundle, filter, search]);

  // Auto-select the first non-done bug once the list has loaded.
  useEffect(() => {
    if (!bundle) return;
    if (selectedKey && bundle.items.some((b) => b.key === selectedKey)) return;
    const firstOpen = bundle.items.find((b) => !b.isDone);
    const target = firstOpen ?? bundle.items[0];
    if (target) void select(target.key);
  }, [bundle, selectedKey, select]);

  const detail = selectedKey ? detailByKey[selectedKey] : undefined;
  const chat = selectedKey ? chatByKey[selectedKey] ?? [] : [];

  const stats = useMemo(() => {
    if (!bundle) return null;
    const open = bundle.items.filter((b) => !b.isDone).length;
    const done = bundle.items.length - open;
    return { open, done, total: bundle.items.length };
  }, [bundle]);

  return (
    <div className="flex-col">
      <div className="card phase-action-card">
        <div className="row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0 }}>Review bugs</h2>
            <p className="muted small" style={{ margin: '4px 0 0' }}>
              Linked to <JiraKeyLink keyId={bundle?.texecKey ?? '...'} className="badge mono small" />
              {stats && (
                <>
                  {' '}· {stats.open} open · {stats.done} done · {stats.total} total
                </>
              )}
            </p>
          </div>
          <span className="spacer" />
          <button className="btn small" disabled={loading} onClick={() => void refresh()}>
            {loading ? 'Refreshing...' : 'Refresh from Jira'}
          </button>
        </div>
        {error && <p style={{ color: 'var(--bad)', marginTop: 8 }}>{error}</p>}
        {bundle?.meta?.stale && (
          <p
            className="small"
            style={{
              marginTop: 8,
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid rgba(210, 153, 34, 0.4)',
              background: 'rgba(210, 153, 34, 0.08)',
              color: 'var(--warn)',
            }}
            title={bundle.meta.staleReason}
          >
            {bundle.meta.staleReason ?? 'Showing cached data; live Jira fetch failed.'}
          </p>
        )}
      </div>

      <div className="bug-review-shell">
        <aside className="bug-review-rail">
          <div className="bug-review-rail-controls">
            <input
              placeholder="Search bugs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
              {(['open', 'all', 'done'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`btn small ${filter === f ? 'btn-primary' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f === 'open' ? 'Non-done' : f === 'done' ? 'Done' : 'All'}
                </button>
              ))}
            </div>
          </div>
          <div className="bug-review-rail-list">
            {!bundle && loading && <p className="muted small">Loading bugs...</p>}
            {bundle && filteredItems.length === 0 && (
              <p className="muted small" style={{ margin: 12 }}>
                {bundle.items.length === 0
                  ? 'No bugs linked to this TEXEC yet.'
                  : 'No bugs match the current filter.'}
              </p>
            )}
            {filteredItems.map((bug) => (
              <BugCard
                key={bug.key}
                bug={bug}
                active={bug.key === selectedKey}
                onSelect={() => void select(bug.key)}
              />
            ))}
          </div>
        </aside>

        <section className="bug-review-detail">
          {!selectedKey && (
            <div className="card">
              <p className="muted">Select a bug from the list to review it.</p>
            </div>
          )}
          {selectedKey && detailLoading && !detail && (
            <div className="card">
              <p className="muted">Loading {selectedKey}...</p>
            </div>
          )}
          {selectedKey && detailError && !detail && (
            <div className="card">
              <p style={{ color: 'var(--bad)' }}>{detailError}</p>
            </div>
          )}
          {detail && (
            <>
              <div className="card">
                <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
                  <JiraKeyLink keyId={detail.key} className="badge mono small" />
                  {detail.status && (
                    <span className={`badge ${detail.isDone ? 'good' : 'info'} small`}>
                      {detail.status}
                    </span>
                  )}
                  {detail.priority && <span className="badge small">{detail.priority}</span>}
                  {detail.linkedTestKey && (
                    <span className="muted small">
                      Raised from{' '}
                      <JiraKeyLink keyId={detail.linkedTestKey} className="badge mono small" />
                    </span>
                  )}
                  <span className="spacer" />
                  {detail.url && (
                    <a className="btn btn-ghost small" href={detail.url} target="_blank" rel="noreferrer">
                      Open in Jira
                    </a>
                  )}
                </div>
                <h3 style={{ marginTop: 8 }}>{detail.summary || '(no summary)'}</h3>
                {detail.labels.length > 0 && (
                  <div className="row" style={{ flexWrap: 'wrap', gap: 4 }}>
                    {detail.labels.map((l) => (
                      <span key={l} className="badge small">
                        {l}
                      </span>
                    ))}
                  </div>
                )}
                <h4 style={{ margin: '12px 0 4px' }}>Description</h4>
                {detail.descriptionMd ? (
                  <div className="ai-card">
                    <ReactMarkdown>{detail.descriptionMd}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="muted small">(no description on Jira)</p>
                )}
                <CommentsThread comments={detail.comments} attachments={detail.attachments} />
              </div>

              <div className="card">
                <BugChat
                  messages={chat}
                  busy={chatBusy}
                  onSend={(q) => void sendChat(detail.key, q)}
                />
              </div>

              <div className="card">
                <BugActionsBar bug={detail} />
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
